const STATS_CACHE_KEY = "dashboard_stats_cache_v1";
const MARKER_SCRIPTS_CACHE_KEY = "dashboard_marker_marked_scripts_v1";
/** Stats older than this are ignored on read; Header also refetches on this interval. */
export const DASHBOARD_STATS_TTL_MS = 30 * 60 * 1000; // 30 minutes
export const DASHBOARD_STATS_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const MIN_STATS_YEAR = 2020;

export function getCurrentStatsYearBounds() {
  const current = new Date().getFullYear();
  return { min: MIN_STATS_YEAR, max: current };
}

/** @param {number} year */
export function isValidStatsYear(year) {
  const { min, max } = getCurrentStatsYearBounds();
  return (
    Number.isInteger(year) && year >= min && year <= max
  );
}

/**
 * @param {string} role 'app-admin' | 'marker'
 * @param {number} year
 * @returns {{ admin_marked?: number, admin_available?: number, assigned_total?: number, marker_assigned?: number, funnel?: object | null } | null}
 */
export function readYearStatsCache(role, year) {
  try {
    const raw = localStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    const doc = JSON.parse(raw);
    const key = `${role}:${year}`;
    const entry = doc[key];
    if (!entry || typeof entry.at !== "number") return null;
    if (Date.now() - entry.at > DASHBOARD_STATS_TTL_MS) return null;
    return entry.payload || null;
  } catch {
    return null;
  }
}

/**
 * @param {string} role
 * @param {number} year
 * @param {object} payload
 */
export function writeYearStatsCache(role, year, payload) {
  try {
    const raw = localStorage.getItem(STATS_CACHE_KEY);
    const doc = raw ? JSON.parse(raw) : {};
    doc[`${role}:${year}`] = { at: Date.now(), payload };
    localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(doc));
  } catch {
    /* quota / private mode */
  }
}


/** Remove cached dashboard payload so the next refresh hits the network. */
export function clearYearStatsCache(role, year) {
  try {
    const raw = localStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return;
    const doc = JSON.parse(raw);
    delete doc[`${role}:${year}`];
    localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(doc));
  } catch {
    /* ignore */
  }
}

export function clearMarkerMarkedScriptsCache() {
  try {
    localStorage.removeItem(MARKER_SCRIPTS_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Full marked-scripts list for marker role (filter by created_at year client-side). */
export function readMarkerMarkedScriptsCache() {
  try {
    const raw = localStorage.getItem(MARKER_SCRIPTS_CACHE_KEY);
    if (!raw) return null;
    const { at, scripts } = JSON.parse(raw);
    if (typeof at !== "number" || !Array.isArray(scripts)) return null;
    if (Date.now() - at > DASHBOARD_STATS_TTL_MS) return null;
    return scripts;
  } catch {
    return null;
  }
}

export function writeMarkerMarkedScriptsCache(scripts) {
  try {
    localStorage.setItem(
      MARKER_SCRIPTS_CACHE_KEY,
      JSON.stringify({ at: Date.now(), scripts: Array.isArray(scripts) ? scripts : [] })
    );
  } catch {
    /* quota */
  }
}

export function countScriptsForReportingYear(scripts, year) {
  if (!Array.isArray(scripts)) return 0;
  return scripts.filter(s => {
    if (!s || !s.created_at) return false;
    const d = new Date(s.created_at);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() === year;
  }).length;
}

const SESSION_YEAR_KEY = "dashboard_stats_year";

export function readPersistedStatsYear() {
  try {
    const v = sessionStorage.getItem(SESSION_YEAR_KEY);
    if (v == null) return null;
    const y = parseInt(v, 10);
    return isValidStatsYear(y) ? y : null;
  } catch {
    return null;
  }
}

export function persistStatsYear(year) {
  try {
    sessionStorage.setItem(SESSION_YEAR_KEY, String(year));
  } catch {
    /* ignore */
  }
}
